import { Card } from "@/components/ui/card";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { CheckCircleIcon, XCircleIcon, MinusCircleIcon } from "@heroicons/react/24/outline";
import { BidDecisionGroup, BidDecision, DecisionEnum, Decision, DecisionActionEnum } from "@/graphql/__generated__/graphql";
import { JSX } from "react/jsx-runtime";
import { useTranslation } from "react-i18next";
import useUserStore from "@/store/shield";
import { CircleProgressIndicator } from "../../Components";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface Props {
  bidDecisionGroup: BidDecisionGroup;
  bidDecisions: BidDecision[];
  isAutoApproved: boolean;
};

/**
 * @component PendingDecisionBar
 * @description Renders a status bar showing the pending bid decision status for participants.
 * It provides feedback on the progress of the decisions, displays the current or final decision,
 * and includes a hoverable indicator for individual participant statuses.
 *
 * @param {Props} props - The props for the component.
 * @param {BidDecisionGroup} props.bidDecisionGroup - The group of bid decisions, including participants and their decisions.
 * @param {BidDecision[]} props.bidDecisions - The array of bid decisions made by participants.
 * @param {boolean} props.isAutoApproved - Whether the revenue has been auto-approved.
 * @returns {JSX.Element} The rendered PendingDecisionBar component.
 */
const PendingDecisionBar = ({ bidDecisionGroup, bidDecisions, isAutoApproved }: Props): JSX.Element => {

  // Hooks
  const { t } = useTranslation(["components", "common"]);
  const awaitingOrganizationId = bidDecisionGroup.awaitingOrganizationId;
  const currentOrgId = useUserStore((state) => state.orgId);

  /**
   * @constant
   * @type {string}
   * @description Message indicating the current status of the bid decision process based on awaiting participant.
   */
  let statusMessage: string = "";
  if (awaitingOrganizationId === null) {
    statusMessage = t("pendingdecisionbar.decisionreached");
  } else if (awaitingOrganizationId === currentOrgId) {
    statusMessage = t("pendingdecisionbar.awaitingdecision");
  } else {
    // Find the awaiting organization name
    const awaitingOrganizationName =  bidDecisionGroup.participantsOnDecisionGroup.find((participant) => participant.id === awaitingOrganizationId)?.organizationName || t("chatbubble.unknownorganization");
    statusMessage = t("pendingdecisionbar.awaitingorganizatioon", { organizationName: awaitingOrganizationName });
  }

  // Determine the decision title based on 'awaitingOrganizationId'
  const decisionTitle = awaitingOrganizationId === null ? t("pendingdecisionbar.finaldecision") : t("pendingdecisionbar.currentdecision");

  // Get bid decisions and participants
  const participants = bidDecisionGroup.participantsOnDecisionGroup || [];
  const filteredBidDecisions = bidDecisions.filter((bd) => bd.action !== DecisionActionEnum.Comment);

  /**
   * @constant
   * @type {number}
   * @description The percentage of participants who have made a decision.
   */
  const totalParticipants: number = participants.length;
  const totalDecisions = new Set(filteredBidDecisions?.map((bd) => bd?.participantId)).size;
  const progress = totalParticipants > 0 ? (totalDecisions / totalParticipants) * 100 : 0;
  const decisionColourMapping: Record<string, string> = {
    [Decision.Bid]: "#84CC16",   // Green for bid
    [Decision.Nobid]: "#EF4444", // Red for nobid
  };

  // Get the current decision from the group
  const currentDecision = bidDecisionGroup.currentDecision || "";

  // Build a map of each organization's latest decision
  const organizationDecisionsMap: { [organizationId: string]: BidDecision } = {};

  // Assume bidDecisions are ordered; the last decision per participant is their latest
  filteredBidDecisions?.forEach((bidDecision) => {
    if (bidDecision?.organizationId) {
      organizationDecisionsMap[bidDecision.organizationId] = bidDecision;
    }
  });

  /**
   * @constant
   * @type {Array}
   * @description Array of participant statuses, each with an organization name and status icon.
   */
  const participantStatuses: Array<any> = participants.map((participant) => {
    const organizationId = participant?.id; // This is actually the organization ID
    const organizationName = participant.organizationName;

    // Get the latest decision for this organization
    const latestBidDecision = organizationDecisionsMap[organizationId];
    // Determine the status icon based on the latest decision
    let statusIcon: JSX.Element;
    if (latestBidDecision) {
      if (latestBidDecision.decision === DecisionEnum.Bid) {
        statusIcon = <CheckCircleIcon className="icon-md text-green-500" />;
      } else if (latestBidDecision.decision === DecisionEnum.Nobid) {
        statusIcon = <XCircleIcon className="icon-md text-red-500" />;
      } else {
        statusIcon = <MinusCircleIcon className="icon-md text-orange-500" />;
      }
    } else {
      // No decision made yet
      statusIcon = <MinusCircleIcon className="icon-md text-gray-500" />;
    }

    return {
      organizationName,
      statusIcon,
    };
  });

  return (
    <Card className="py-4 px-4 flex flex-row items-center justify-between text-sm">
      <div>
        {/* Display the appropriate status message */}
        <p
          className={cn(
            awaitingOrganizationId === currentOrgId
              ? "text-yellow-500"
              : "text-gray-400"
          )}
        >
          {statusMessage}
        </p>
      </div>
      <div className="flex items-center gap-2 font-semibold">
        {/* Display 'Final Decision' or 'Current Decision' based on the state */}
        <p>{decisionTitle}</p>
        <HoverCard openDelay={0}>
          <HoverCardTrigger className="flex items-center justify-center cursor-help">
            {/* Progress Indicator with calculated progress */}
            <Button
              variant="ghost"
              size="icon"
              className="w-[22px] h-fit p-0 hover:cursor-help"
              type="button"
            >
              <CircleProgressIndicator
                progress={isAutoApproved ? 100 : progress}
                tenderDecision={currentDecision}
                colourMapping={decisionColourMapping}
              />
            </Button>
          </HoverCardTrigger>
          {!isAutoApproved && (
            <HoverCardContent className="min-w-[250px] w-auto max-w-[400px]" align="end" side="top">
              <div className="flex-row space-y-2">
                {/* Dynamically render participant statuses */}
                {participantStatuses.map((participantStatus, index) => (
                  <div key={index} className="flex items-center gap-2">
                    {participantStatus.statusIcon}
                    <p>{participantStatus.organizationName}</p>
                  </div>
                ))}
              </div>
            </HoverCardContent>
          )}
        </HoverCard>
        {/* Display the current tender decision */}
        <p>
          {currentDecision === DecisionEnum.Bid
            ? t("common:bid") :
            currentDecision === DecisionEnum.Nobid
              ? t("common:nobid") : ""
          }
        </p>
      </div>
    </Card>
  );
};

export default PendingDecisionBar;
